import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Section, Card, Badge, Button, Input, Label, Alert, Table } from "../design-system";
import { useAuth } from "../auth/AuthProvider";
import { Search, ArrowLeft, ArrowRight, Check, User, MapPin, Package, Receipt } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
function money(v) { return v === null || v === undefined ? "—" : `€ ${Number(v).toFixed(2)}`; }

const STEPS = [
  { key: "customer", label: "Cliente", icon: User },
  { key: "dp",       label: "Punto consegna", icon: MapPin },
  { key: "catalog",  label: "Catalogo & Carrello", icon: Package },
  { key: "confirm",  label: "Conferma", icon: Receipt },
];

function StepIndicator({ current }) {
  const idx = STEPS.findIndex((s) => s.key === current);
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}
         data-testid="sales-new-order-stepper">
      {STEPS.map((s, i) => {
        const done = i < idx, active = i === idx;
        const Ico = s.icon;
        const bg = done ? "#16a34a" : active ? "#1d4ed8" : "#e2e8f0";
        const fg = done || active ? "#fff" : "#64748b";
        return (
          <div key={s.key} data-testid={`step-${s.key}`}
               style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px",
                        borderRadius: 8, background: bg, color: fg, fontSize: ".85rem", fontWeight: 500 }}>
            {done ? <Check size={14} /> : <Ico size={14} />} {i + 1}. {s.label}
          </div>
        );
      })}
    </div>
  );
}

// ---------- Step 1: Customer ----------
function StepCustomer({ onNext }) {
  const { apiGet } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet("/api/customers").then((r) => { setItems(r); setLoading(false); }).catch(() => setLoading(false));
  }, [apiGet]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const k = q.trim().toLowerCase();
    return items.filter((c) =>
      (c.business_name || "").toLowerCase().includes(k)
      || (c.code || "").toLowerCase().includes(k)
      || (c.vat_number || "").toLowerCase().includes(k)
      || (c.email || "").toLowerCase().includes(k)
    );
  }, [q, items]);

  return (
    <Section title="Step 1 · Seleziona il cliente"
             subtitle="Cerca per ragione sociale, codice o Partita IVA">
      <Card>
        <Card.Body>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <Search size={14} style={{ position: "absolute", left: 10, top: 12, color: "#94a3b8" }} />
              <Input data-testid="sno-customer-search" placeholder="Cerca cliente…"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     style={{ paddingLeft: 32 }} autoFocus />
            </div>
          </div>
          {loading && <div className="muted">Caricamento clienti…</div>}
          {!loading && filtered.length === 0 && (
            <Alert tone="info">Nessun cliente trovato per «{q}».</Alert>
          )}
          <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6 }}>
            {filtered.map((c) => (
              <div key={c.id} data-testid={`sno-customer-row-${c.code}`}
                   onClick={() => onNext(c)}
                   style={{ padding: 10, borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}
                   onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                   onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <strong>{c.business_name}</strong>{" "}
                  <span className="muted" style={{ fontSize: ".8rem" }}>· {c.code}</span>
                  <div className="muted" style={{ fontSize: ".78rem" }}>
                    {c.vat_number ? `P.IVA ${c.vat_number}` : ""}
                    {c.email ? ` · ${c.email}` : ""}
                    {c.region ? ` · ${c.region}` : ""}
                  </div>
                </div>
                <Button size="sm" variant="primary" data-testid={`sno-pick-customer-${c.code}`}>
                  Seleziona <ArrowRight size={12} />
                </Button>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    </Section>
  );
}

// ---------- Step 2: Delivery Point ----------
function StepDeliveryPoint({ customer, onBack, onNext }) {
  const { apiGet } = useAuth();
  const [dps, setDps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    apiGet(`/api/delivery-points?customer_id=${customer.id}`)
      .then((r) => { setDps(r || []); setLoading(false); })
      .catch((e) => { setErr(e?.data?.detail || "Errore caricamento punti di consegna"); setLoading(false); });
  }, [apiGet, customer.id]);

  return (
    <Section title="Step 2 · Seleziona il punto di consegna"
             subtitle={`Cliente: ${customer.business_name} · ${customer.code}`}>
      <Card>
        <Card.Body>
          {loading && <div className="muted">Caricamento punti di consegna…</div>}
          {err && <Alert tone="danger">{err}</Alert>}
          {!loading && dps.length === 0 && (
            <Alert tone="warning">
              Il cliente selezionato non ha punti di consegna attivi. Crea prima un DP dall'area anagrafica.
            </Alert>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
            {dps.map((d) => (
              <Card key={d.id} data-testid={`sno-dp-card-${d.id.slice(0, 8)}`}
                    style={{ cursor: "pointer", borderColor: "#cbd5e1" }}
                    onClick={() => onNext(d)}>
                <Card.Body style={{ padding: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                    <strong>{d.name}</strong>
                    {d.is_default && <Badge tone="success">Default</Badge>}
                  </div>
                  <div className="muted" style={{ fontSize: ".82rem", marginTop: 4 }}>
                    {[d.address, d.city, d.province, d.postal_code].filter(Boolean).join(" · ")}
                  </div>
                  <div className="muted" style={{ fontSize: ".78rem", marginTop: 4 }}>
                    Regione: {d.region || "—"}
                  </div>
                  <Button size="sm" variant="primary" style={{ marginTop: 8, width: "100%" }}
                          data-testid={`sno-pick-dp-${d.id.slice(0, 8)}`}>
                    Seleziona <ArrowRight size={12} />
                  </Button>
                </Card.Body>
              </Card>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <Button size="sm" variant="outline" onClick={onBack} data-testid="sno-dp-back">
              <ArrowLeft size={12} /> Indietro
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Section>
  );
}

// ---------- Step 3: Catalog + Cart (local) ----------
function StepCatalog({ customer, dp, cart, setCart, onBack, onNext }) {
  const { apiGet } = useAuth();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [added, setAdded] = useState(null);

  const load = async (query = "") => {
    setLoading(true); setErr(null);
    try {
      const p = query
        ? `q=${encodeURIComponent(query)}&customer_id=${customer.id}`
        : `customer_id=${customer.id}`;
      setProducts(await apiGet(`/api/catalog?${p}`));
    } catch (e) { setErr(e?.data?.detail || "Errore catalogo"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, []);

  const addLines = (p, qty) => {
    setCart((s) => {
      const existing = s.find((l) => l.product_id === p.id);
      if (existing) {
        return s.map((l) => l.product_id === p.id ? { ...l, qty_cartons: l.qty_cartons + qty } : l);
      }
      return [...s, {
        product_id: p.id, sku: p.sku, name: p.name,
        qty_cartons: qty, kg_per_carton: p.kg_per_carton,
        unit_price: p.unit_price, cartons_per_layer: p.cartons_per_layer,
        cartons_per_std_pallet: p.cartons_per_std_pallet,
      }];
    });
    setAdded(p.sku);
    setTimeout(() => setAdded(null), 1200);
  };

  const localKg = cart.reduce((s, l) => s + (Number(l.qty_cartons) || 0) * (Number(l.kg_per_carton) || 0), 0);
  const totalCartons = cart.reduce((s, l) => s + l.qty_cartons, 0);

  return (
    <Section title="Step 3 · Componi l'ordine dal catalogo"
             subtitle={`Cliente: ${customer.business_name} · DP: ${dp.name} · ${cart.length} articoli · ${totalCartons} cartoni · ${localKg.toFixed(1)} kg`}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 12 }}>
        {/* Catalog panel */}
        <Card>
          <Card.Body>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <Input data-testid="sno-catalog-search" placeholder="Cerca SKU o nome…"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") load(q); }} />
              <Button variant="outline" onClick={() => load(q)} data-testid="sno-catalog-search-btn">
                Cerca
              </Button>
            </div>
            {err && <Alert tone="danger">{err}</Alert>}
            {loading && <div className="muted">Caricamento…</div>}
            <div style={{ maxHeight: 480, overflowY: "auto" }} data-testid="sno-catalog-list">
              {products.map((p) => {
                const layer = Number(p.cartons_per_layer) || 0;
                const step = layer >= 1 ? layer : 1;
                return (
                  <div key={p.id} data-testid={`sno-product-${p.sku}`}
                       style={{ padding: 8, borderBottom: "1px solid #f1f5f9",
                                display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, alignItems: "center" }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="kicker" style={{ fontSize: ".72rem" }}>{p.sku}</div>
                      <strong style={{ fontSize: ".9rem" }}>{p.name}</strong>
                      <div className="muted" style={{ fontSize: ".76rem" }}>
                        {p.kg_per_carton ? `${p.kg_per_carton} kg/cart.` : ""}
                        {p.cartons_per_layer ? ` · strato: ${p.cartons_per_layer}` : ""}
                        {p.cartons_per_std_pallet ? ` · pedana: ${p.cartons_per_std_pallet}` : ""}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {p.unit_price != null ? (
                        <>
                          <div style={{ fontWeight: 600 }} data-testid={`sno-price-${p.sku}`}>
                            {money(p.unit_price)}<span style={{ fontSize: ".72rem", opacity: .7 }}> /kg</span>
                          </div>
                          <div className="muted" style={{ fontSize: ".7rem" }}>{p.price_list_code || ""}</div>
                        </>
                      ) : <div className="muted" style={{ fontSize: ".78rem" }}>no price</div>}
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <Button size="sm" data-testid={`sno-add-${p.sku}`}
                              onClick={() => addLines(p, step)}
                              title={step > 1 ? `+1 strato (${step} cart.)` : "+1 cartone"}>
                        {added === p.sku ? "✓" : (step > 1 ? `+ Strato (${step})` : "+ Cart.")}
                      </Button>
                      {p.cartons_per_std_pallet > 0 && (
                        <Button size="sm" variant="outline"
                                data-testid={`sno-add-pallet-${p.sku}`}
                                onClick={() => addLines(p, Number(p.cartons_per_std_pallet))}
                                title={`Aggiunge ${p.cartons_per_std_pallet} cartoni (pedana intera)`}>
                          Pedana
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {!loading && products.length === 0 && (
                <div className="muted" style={{ padding: 20, textAlign: "center" }}>Nessun prodotto</div>
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Cart panel */}
        <Card data-testid="sno-cart-panel">
          <Card.Header>
            <strong>Carrello ({cart.length})</strong>
            <span className="muted" style={{ marginLeft: 8, fontSize: ".78rem" }}>{localKg.toFixed(1)} kg</span>
          </Card.Header>
          <Card.Body style={{ padding: 8 }}>
            {cart.length === 0 && (
              <div className="muted" style={{ padding: 16, textAlign: "center", fontSize: ".85rem" }}>
                Nessun articolo. Aggiungi dal catalogo.
              </div>
            )}
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              {cart.map((l) => (
                <div key={l.product_id} data-testid={`sno-cart-row-${l.sku}`}
                     style={{ padding: 6, borderBottom: "1px solid #f1f5f9", fontSize: ".82rem" }}>
                  <div><strong>{l.sku}</strong> — {l.name}</div>
                  <div style={{ display: "flex", gap: 4, marginTop: 4, alignItems: "center" }}>
                    <Button size="sm" variant="outline" data-testid={`sno-qty-minus-${l.sku}`}
                            onClick={() => setCart((s) => s.map((x) => x.product_id === l.product_id
                              ? { ...x, qty_cartons: Math.max(1, x.qty_cartons - 1) } : x))}>−</Button>
                    <input type="number" min="1" value={l.qty_cartons}
                           data-testid={`sno-qty-${l.sku}`}
                           style={{ width: 56, textAlign: "center", padding: "2px 4px" }}
                           onChange={(e) => {
                             const v = parseInt(e.target.value, 10);
                             if (Number.isInteger(v) && v >= 1)
                               setCart((s) => s.map((x) => x.product_id === l.product_id ? { ...x, qty_cartons: v } : x));
                           }} />
                    <Button size="sm" variant="outline" data-testid={`sno-qty-plus-${l.sku}`}
                            onClick={() => setCart((s) => s.map((x) => x.product_id === l.product_id
                              ? { ...x, qty_cartons: x.qty_cartons + 1 } : x))}>+</Button>
                    <span className="muted" style={{ marginLeft: "auto", fontSize: ".75rem" }}>
                      {(l.qty_cartons * l.kg_per_carton).toFixed(1)} kg
                    </span>
                    <Button size="sm" variant="ghost" data-testid={`sno-remove-${l.sku}`}
                            onClick={() => setCart((s) => s.filter((x) => x.product_id !== l.product_id))}>
                      ✕
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
          <Card.Footer>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
              <Button size="sm" variant="outline" onClick={onBack} data-testid="sno-catalog-back">
                <ArrowLeft size={12} /> Indietro
              </Button>
              <Button size="sm" variant="primary"
                      disabled={cart.length === 0}
                      onClick={onNext} data-testid="sno-catalog-next">
                Continua <ArrowRight size={12} />
              </Button>
            </div>
          </Card.Footer>
        </Card>
      </div>
    </Section>
  );
}

// ---------- Step 4: Confirm ----------
// Helper: mappa product_id → line result dal pricing preview.
function _linesById(preview) {
  if (!preview?.lines) return {};
  const out = {};
  for (const ln of preview.lines) out[ln.product_id] = ln;
  return out;
}

function StepConfirm({ customer, dp, cart, setCart, onBack, onCreated }) {
  const { apiPost } = useAuth();
  // apiPost è ricreato ogni render → uso ref stabile per usarlo dentro l'effect
  // senza inserirlo nelle deps (che causerebbe loop infinito di re-fire).
  const apiPostRef = useRef(apiPost);
  useEffect(() => { apiPostRef.current = apiPost; }, [apiPost]);

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [requestedDate, setRequestedDate] = useState("");
  const [advancePayment, setAdvancePayment] = useState(false);
  const [notes, setNotes] = useState("");

  const previewBody = useMemo(() => ({
    customer_id: customer.id,
    delivery_mode: "DELIVERY",
    delivery_point_id: dp.id,
    advance_payment: advancePayment,
    requested_delivery_date: requestedDate || null,
    lines: cart.map((l) => ({ product_id: l.product_id, qty_cartons: l.qty_cartons })),
  }), [customer.id, dp.id, cart, advancePayment, requestedDate]);

  // Pricing preview via apiPost (stesso pattern di /login, /orders, /catalog).
  useEffect(() => {
    if (cart.length === 0) { setPreview(null); setLoading(false); return; }
    setLoading(true); setErr(null);
    let cancelled = false;
    /* eslint-disable no-console */
    console.log("[SNO-DBG] useEffect fired, calling apiPost", { qtys: cart.map(l => l.qty_cartons) });
    const p = apiPostRef.current("/api/pricing/preview", previewBody);
    console.log("[SNO-DBG] apiPost returned promise:", p);
    p.then((data) => {
      console.log("[SNO-DBG] .then fired, cancelled=", cancelled, "data keys:", data && Object.keys(data));
      if (cancelled) return;
      setPreview(data); setLoading(false);
    }).catch((e) => {
      console.log("[SNO-DBG] .catch fired, cancelled=", cancelled, "err:", e?.message, e);
      if (cancelled) return;
      setErr(e?.data?.detail || e?.message || "Errore pricing preview"); setLoading(false);
    });
    return () => { console.log("[SNO-DBG] cleanup"); cancelled = true; };
    /* eslint-enable no-console */
  }, [previewBody, cart.length]);

  const linesById = _linesById(preview);

  const changeQty = (product_id, delta_or_val, absolute = false) => {
    setCart((s) => s.map((x) => {
      if (x.product_id !== product_id) return x;
      const cur = x.qty_cartons || 0;
      const next = absolute ? Number(delta_or_val) : (cur + Number(delta_or_val));
      return { ...x, qty_cartons: Math.max(1, Number.isFinite(next) ? next : cur) };
    }));
  };

  const removeLine = (product_id) => {
    setCart((s) => s.filter((x) => x.product_id !== product_id));
  };

  const submit = async () => {
    setSubmitting(true); setErr(null);
    try {
      const orderBody = {
        customer_id: customer.id,
        delivery_mode: "DELIVERY",
        delivery_point_id: dp.id,
        advance_payment: advancePayment,
        requested_delivery_date: requestedDate || null,
        lines: cart.map((l) => ({ product_id: l.product_id, qty_cartons: l.qty_cartons })),
        notes_customer: notes || null,
      };
      const created = await apiPost("/api/orders", orderBody);
      onCreated(created);
    } catch (e) {
      setErr(e?.data?.detail || "Errore conferma ordine");
    } finally { setSubmitting(false); }
  };

  return (
    <Section title="Step 4 · Conferma ordine per conto del cliente"
             subtitle={`Cliente: ${customer.business_name} · DP: ${dp.name}`}>
      <Card>
        <Card.Body>
          <Alert tone="info" data-testid="sno-audit-note">
            <strong>Audit interno:</strong> l'ordine verrà registrato a nome del cliente <strong>{customer.business_name}</strong>.
            Nell'audit backoffice comparirà il tuo utente come "Creato da".
            Il cliente lo vedrà come un normale proprio ordine nel portale.
          </Alert>

          {/* Meta */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                        gap: 12, marginTop: 12 }}>
            <div>
              <Label>Data richiesta consegna</Label>
              <Input type="date" data-testid="sno-requested-date"
                     value={requestedDate} onChange={(e) => setRequestedDate(e.target.value)} />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: ".9rem" }}>
                <input type="checkbox" data-testid="sno-advance-payment"
                       checked={advancePayment}
                       onChange={(e) => setAdvancePayment(e.target.checked)} />
                Pagamento anticipato
              </label>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Label>Note ordine (opzionale)</Label>
              <Input data-testid="sno-notes" value={notes}
                     onChange={(e) => setNotes(e.target.value)}
                     placeholder="Note per la consegna o referenze cliente" />
            </div>
          </div>

          {/* Lines — tabella editabile con prezzi €/kg da pricing preview */}
          <div style={{ marginTop: 16, overflowX: "auto" }} data-testid="sno-confirm-lines-wrap">
            <table className="ds-table" style={{ width: "100%", fontSize: ".85rem" }} data-testid="sno-confirm-lines">
              <thead>
                <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                  <th style={{ padding: "6px 8px" }}>SKU</th>
                  <th style={{ padding: "6px 8px" }}>Prodotto</th>
                  <th style={{ padding: "6px 8px", textAlign: "center", width: 150 }}>Cartoni</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>kg/cart.</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>kg tot.</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>€/kg</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>Totale riga</th>
                  <th style={{ padding: "6px 8px", width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {cart.map((l) => {
                  const pl = linesById[l.product_id];
                  const layer = Number(l.cartons_per_layer) || 0;
                  const kgTot = (l.qty_cartons * l.kg_per_carton).toFixed(1);
                  const unitBase = pl ? Number(pl.unit_price_base) : null;
                  const unitEff  = pl ? Number(pl.unit_price_effective) : null;
                  const hasDisc = pl && Number(pl.line_discount) > 0;
                  const notLayerMultiple = layer >= 1 && (l.qty_cartons % layer !== 0);
                  const missingToLayer = notLayerMultiple ? (layer - (l.qty_cartons % layer)) : 0;
                  return (
                    <tr key={l.product_id} data-testid={`sno-confirm-row-${l.sku}`}
                        style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "6px 8px", fontFamily: "ui-monospace,monospace", fontSize: ".78rem" }}>{l.sku}</td>
                      <td style={{ padding: "6px 8px" }}>{l.name}</td>
                      <td style={{ padding: "6px 8px", textAlign: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
                          <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                            <Button size="sm" variant="outline" data-testid={`sno-confirm-minus-${l.sku}`}
                                    onClick={() => changeQty(l.product_id, -1)}
                                    title="−1 cartone">−</Button>
                            <input type="number" min="1" value={l.qty_cartons}
                                   data-testid={`sno-confirm-qty-${l.sku}`}
                                   style={{ width: 60, textAlign: "center", padding: "2px 4px", border: "1px solid #cbd5e1", borderRadius: 4 }}
                                   onChange={(e) => {
                                     const v = parseInt(e.target.value, 10);
                                     if (Number.isInteger(v) && v >= 1) changeQty(l.product_id, v, true);
                                   }} />
                            <Button size="sm" variant="outline" data-testid={`sno-confirm-plus-${l.sku}`}
                                    onClick={() => changeQty(l.product_id, +1)}
                                    title="+1 cartone">+</Button>
                          </div>
                          {notLayerMultiple && (
                            <div className="muted" data-testid={`sno-confirm-hint-layer-${l.sku}`}
                                 style={{ fontSize: ".7rem", color: "#64748b" }}>
                              +{missingToLayer} per strato ({layer})
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{Number(l.kg_per_carton).toFixed(2)}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }} data-testid={`sno-confirm-kg-${l.sku}`}>{kgTot}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }} data-testid={`sno-confirm-unitprice-${l.sku}`}>
                        {unitEff != null ? (
                          hasDisc ? (
                            <>
                              <span style={{ textDecoration: "line-through", opacity: .55, marginRight: 4 }}>
                                {money(unitBase)}
                              </span>
                              <strong>{money(unitEff)}</strong>
                            </>
                          ) : <>{money(unitEff)}</>
                        ) : <span className="muted">—</span>}
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }} data-testid={`sno-confirm-linetotal-${l.sku}`}>
                        {pl ? <strong>{money(pl.line_total)}</strong> : <span className="muted">—</span>}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        <Button size="sm" variant="ghost" data-testid={`sno-confirm-remove-${l.sku}`}
                                onClick={() => removeLine(l.product_id)} title="Rimuovi riga">
                          ✕
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {cart.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 20, textAlign: "center", color: "#64748b" }}>
                    Nessuna riga. Torna al catalogo per aggiungere prodotti.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pricing summary */}
          {loading && <div className="muted" style={{ marginTop: 10 }} data-testid="sno-pricing-recalc">Ricalcolo prezzi…</div>}
          {err && <Alert tone="danger" data-testid="sno-error">{err}</Alert>}
          {preview && (
            <div style={{ marginTop: 16, padding: 12, border: "1px solid #e2e8f0", borderRadius: 6 }}
                 data-testid="sno-pricing-summary">
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Subtotale merce</span><span>{money(preview.subtotal)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Sconti</span><span>-{money(preview.discount_total)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Trasporto</span><span>{money(preview.shipping_cost)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8,
                            paddingTop: 8, borderTop: "1px solid #e2e8f0", fontSize: "1.1rem", fontWeight: 600 }}>
                <span>Totale</span>
                <span data-testid="sno-total">{money(preview.total)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: ".85rem" }}>
                <span className="muted">Kg totali</span>
                <span data-testid="sno-total-kg"><strong>{Number(preview.total_weight_kg).toFixed(1)}</strong> kg</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".85rem" }}>
                <span className="muted">Pallet stimati</span>
                <span data-testid="sno-total-pallets">
                  {preview.estimated_pallets != null
                    ? <><strong>{Number(preview.estimated_pallets).toFixed(2)}</strong>&nbsp;pallet</>
                    : <span className="muted">—</span>}
                </span>
              </div>
              <div className="muted" style={{ fontSize: ".72rem", marginTop: 4, fontStyle: "italic" }}>
                Il numero pallet indicato è stimato dalla pallettizzazione dichiarata (cartoni per pedana / packaging profile). I pallet effettivi verranno confermati dalla Logistica in fase di consuntivazione.
              </div>
            </div>
          )}
        </Card.Body>
        <Card.Footer>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Button variant="outline" onClick={onBack} loading={submitting} disabled={submitting} data-testid="sno-confirm-back">
              <ArrowLeft size={12} /> Indietro
            </Button>
            <Button variant="primary" size="lg" onClick={submit}
                    loading={submitting} disabled={submitting || cart.length === 0 || !preview || loading}
                    data-testid="sno-confirm-submit">
              {submitting ? "Creazione…" : (loading ? "Attendo ricalcolo…" : "Crea ordine per il cliente")}
            </Button>
          </div>
        </Card.Footer>
      </Card>
    </Section>
  );
}

// ---------- Wizard container ----------
export default function SalesNewOrderPage() {
  const nav = useNavigate();
  const [step, setStep] = useState("customer");
  const [customer, setCustomer] = useState(null);
  const [dp, setDp] = useState(null);
  const [cart, setCart] = useState([]);
  const [createdOrder, setCreatedOrder] = useState(null);

  const onCreated = (o) => {
    setCreatedOrder(o);
    // Cleanup local state after successful order creation
    setCart([]);
  };

  if (createdOrder) {
    return (
      <Section title="Ordine creato con successo"
               subtitle={`Ordine ${createdOrder.number} per ${customer.business_name}`}>
        <Card>
          <Card.Body>
            <Alert tone="success" data-testid="sno-success">
              <strong>Ordine creato: {createdOrder.number}</strong>
              <div style={{ marginTop: 4 }}>
                Stato: <strong>{createdOrder.status}</strong> · Totale: <strong>{money(createdOrder.total)}</strong>
              </div>
              <div style={{ marginTop: 4, fontSize: ".85rem" }}>
                L'ordine è ora visibile nel portale del cliente e nel Kanban Sales.
                Le notifiche email (cliente + agente in CC + ufficio interno) sono state accodate.
              </div>
            </Alert>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <Button variant="primary" onClick={() => nav("/sales")} data-testid="sno-goto-sales">
                Torna al Kanban Sales
              </Button>
              <Button variant="outline"
                      onClick={() => {
                        setCreatedOrder(null); setCustomer(null); setDp(null); setCart([]);
                        setStep("customer");
                      }} data-testid="sno-new-again">
                Crea un altro ordine
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Section>
    );
  }

  return (
    <>
      <Section title="Nuovo ordine per conto del cliente"
               subtitle="Sales inserisce un ordine ricevuto via telefono / email a nome del cliente"
               data-testid="sales-new-order-page">
        <StepIndicator current={step} />
      </Section>

      {step === "customer" && (
        <StepCustomer onNext={(c) => { setCustomer(c); setStep("dp"); }} />
      )}
      {step === "dp" && customer && (
        <StepDeliveryPoint customer={customer}
                           onBack={() => setStep("customer")}
                           onNext={(d) => { setDp(d); setStep("catalog"); }} />
      )}
      {step === "catalog" && customer && dp && (
        <StepCatalog customer={customer} dp={dp} cart={cart} setCart={setCart}
                     onBack={() => setStep("dp")}
                     onNext={() => setStep("confirm")} />
      )}
      {step === "confirm" && customer && dp && (
        <StepConfirm customer={customer} dp={dp} cart={cart} setCart={setCart}
                     onBack={() => setStep("catalog")}
                     onCreated={onCreated} />
      )}
    </>
  );
}
